import { Box, Card, CircularProgress } from "@mui/material";
import { Col, Form, message, Row } from "antd";
import { useForm } from "antd/es/form/Form";
import ActionButton from "../../components/common/buttons/ActionButton";
import CancelButton from "../../components/common/buttons/CancelButton";
import InputField from "../../components/common/fields/InputField";
import SelectField from "../../components/common/fields/SelectField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import GoogleMap from "../../components/GoogleMap";
import api from "../../hooks/api";
import { useCitys, useCountries, useDistrict } from "../../hooks/useCountries";
import { useLanguages } from "../../hooks/useLanguages";
import { useBussinessTypes } from "../../hooks/useSalon";
import { pathAPI } from "../../utils/pathApi";
import { SalonAPI } from "../../api/Salon";

// Adapts an id-based handler to SelectField's onChange (ignores "cleared" events)
const onSelectId =
  (handler: (id: number) => void) => (value: string | number | null) => {
    if (value !== null) handler(Number(value));
  };

const InfoTab = ({
  infoData,
  onChange,
  onNext,
  isLoadingData
}: {
  infoData: SalonInfo | null;
  onChange?: (data: SalonInfo) => void;
  onNext?: (tabName: string) => void;
  isLoadingData?: boolean;
}) => {
  const { t } = useTranslation();
  const [infoForm] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();
  const [selectedCoutry, setSelectedCoutry] = useState<number | null>(null);
  const [selectedBankCoutry, setSelectedBankCoutry] = useState<number | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [latLongPosition, setLatLongPosition] = useState({
    lat: 10.776888,
    lng: 106.700897,
  });
  const [{ data: countries, isLoading: coutriesLoading }] = useCountries();
  const [{ data: countryOfBank, isLoading: loadingBankCountry }] =
    useCountries();
  const [{ data: language, isLoading: languageLoading }] = useLanguages();

  const [{ data: businessTypes, isLoading: loadingBussiness }] =
    useBussinessTypes();
  const [{ data: cities, isLoading: citiesLoading }] = useCitys({
    slug: countries?.find((c) => c.id === selectedCoutry)?.id?.toString() || "",
  });
  const [{ data: cityOfBank, isLoading: loadingCitiesBankInfo }] = useCitys({
    slug:
      countryOfBank?.find((c) => c.id === selectedBankCoutry)?.id?.toString() ||
      "",
  });
  const [{ data: districts, isLoading: districtsLoading }] = useDistrict({
    slug: countries?.find((c) => c.id === selectedCoutry)?.id?.toString() || "",
    id: selectedCity || "",
  });

  const handleSubmit = async () => {
    if (!id) return;
    setLoading(true);
    const values = infoForm.getFieldsValue();
    SalonAPI.updateSalonInfo(id || "", values).then(() => {
      message.success(t("salon.info.messages.update_success"));
      navigate("/salon");
    }).catch(() => message.error(t("salon.info.messages.error")))
    .finally(() => setLoading(false));
  };

  const getCountryLabel = (country: any): string => {
    if (country.i18n_names?.en !== undefined) {
      return `${country.i18n_names.en || ""} (${country.country_code || ""})`;
    }
    return `${country.name || ""} (${country.code || ""})`;
  };

  const handleSelectDistrict = (districtId: number) => {
    infoForm.setFieldValue(["address", "district"], districtId);
    const findDistrict: any = districts?.find((d) => d.id === districtId);
    setLatLongPosition({
      lat: findDistrict?.lat || 10.776888,
      lng: findDistrict?.lon || 106.700897,
    });
  };

  const handleSelectCountry = (countryId: number) => {
    setSelectedCoutry(countryId);
    infoForm.setFieldValue(["address", "country"], countryId);
    // Clear city and district when country changes
    infoForm.setFieldValue(["address", "city"], undefined);
    infoForm.setFieldValue(["address", "district"], undefined);
    setSelectedCity(null);
    const findCountry: any = countries?.find((c) => c.id === countryId);
    setLatLongPosition({
      lat: findCountry?.lat || 10.776888,
      lng: findCountry?.lon || 106.700897,
    });
  };

  const handleSelectCity = (cityId: number) => {
    infoForm.setFieldValue(["address", "city"], cityId);
    setSelectedCity(cityId.toString());
    // Clear district when city changes
    infoForm.setFieldValue(["address", "district"], undefined);
    const findCity: any = cities?.find((c) => c.id === cityId);
    setLatLongPosition({
      lat: findCity?.lat || 10.776888,
      lng: findCity?.lon || 106.700897,
    });
  };

  const handleSelectCountryBankInfo = (countryId: number) => {
    setSelectedBankCoutry(countryId);
  };

  useEffect(() => {
    if (!infoData) return;

    // Transform nested objects to IDs
    const transformedData = {
      ...infoData,
      address: {
        ...infoData.address,
        country: typeof infoData.address?.country === 'object'
          ? infoData.address?.country?.id
          : infoData.address?.country,
        city: typeof infoData.address?.city === 'object'
          ? infoData.address?.city?.id
          : infoData.address?.city,
        district: typeof infoData.address?.district === 'object'
          ? infoData.address?.district?.id
          : infoData.address?.district,
      },
      person_in_charge: {
        ...infoData.person_in_charge,
        language: typeof infoData.person_in_charge?.language === 'object'
          ? infoData.person_in_charge?.language?.id
          : infoData.person_in_charge?.language,
      },
    };

    // Set form values with transformed data
    infoForm.setFieldsValue(transformedData);

    // Set state for dependent dropdowns
    if (infoData.address?.country?.id) {
      setSelectedCoutry(infoData.address.country.id);
    }
    if (infoData.address?.city?.id) {
      setSelectedCity(infoData.address.city.id?.toString());
    }
    if ((infoData as any).bank_info?.bank_country?.id) {
      setSelectedBankCoutry((infoData as any).bank_info.bank_country.id);
    } else if ((infoData as any).bank?.bank_country?.id) {
      setSelectedBankCoutry((infoData as any).bank.bank_country.id);
    }

    // Set lat/long position
    if (infoData.address?.lat && infoData.address?.lon) {
      setLatLongPosition({
        lat: infoData.address.lat,
        lng: infoData.address.lon,
      });
    }

    handleFieldsChange();
  }, [infoData]);

  const handleValuesChange = (_: any, allValues: any) => {
    if (!id && onChange) onChange(allValues);
  };

  const handleContinue = () => {
    onNext?.("Photos");
  };

  const handleFieldsChange = () => {
    const hasErrors = infoForm.getFieldsError().some(field => field.errors.length > 0);
    setIsFormValid(hasErrors);
  };

  return (
    <>
    {isLoadingData ? (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    ) : (
      <Form form={infoForm} onValuesChange={handleValuesChange}>
        {/* ABOUT THE SALON */}
        <Box component={Card} mt={3} className="p-4">
          <Box fontWeight={"bold"} fontSize="20px" mb={2}>
            {t("salon.info.sections.about")}
          </Box>
          <Row gutter={[12, 12]}>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.business_name")}
                name={["business", "business_name"]}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: t("salon.info.validation.business_name") }]}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.business_type")}
                name={["business", "business_type"]}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: t("salon.info.validation.business_type") }]}
              >
                <SelectField
                  loading={loadingBussiness}
                  options={(businessTypes || []).map((i) => ({
                    value: i.id,
                    label: i.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.business_phone")}
                required
                name={["business", "business_phone"]}
                rules={[
                  { required: true, message: t("salon.info.validation.business_phone") },
                  {
                    validator: (_, value) => {
                      if (!value || value >= 0) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("salon.info.validation.business_phone_negative")));
                    }
                  }
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField type="number" inputProps={{ min: 0 }} />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.num_of_employee")}
                required
                name={["business", "num_of_employee"]}
                rules={[
                  { required: true, message: t("salon.info.validation.num_of_employee") },
                  {
                    validator: (_, value) => {
                      if (!value || value >= 0) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("salon.info.validation.num_of_employee_negative")));
                    }
                  }
                ]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField type="number" inputProps={{ min: 0 }} />
              </Form.Item>
            </Col>
            <Col md={24} span={24} lg={24} xs={24}>
              <Form.Item
                label={t("salon.info.fields.description")}
                name={["business", "description"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField multiline minRows={10} />
              </Form.Item>
            </Col>
          </Row>
        </Box>

        {/* ADDRESS */}
        <Box component={Card} mt={3} className="p-4">
          <Box fontWeight={"bold"} fontSize="20px" mb={2}>
            {t("salon.info.sections.address")}
          </Box>
          <Row gutter={[12, 12]}>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.country")}
                name={["address", "country"]}
                required
                rules={[{ required: true, message: t("salon.info.validation.country") }]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <SelectField
                  onChange={onSelectId(handleSelectCountry)}
                  loading={coutriesLoading}
                  options={(countries || []).map((i) => ({
                    value: i.id,
                    label: getCountryLabel(i),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.city")}
                name={["address", "city"]}
                required
                rules={[{ required: true, message: t("salon.info.validation.city") }]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <SelectField
                  onChange={onSelectId(handleSelectCity)}
                  loading={citiesLoading}
                  options={(cities || []).map((i) => ({
                    value: i.id,
                    label: i.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.district")}
                name={["address", "district"]}
                required
                rules={[{ required: true, message: t("salon.info.validation.district") }]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <SelectField
                  loading={districtsLoading}
                  onChange={onSelectId(handleSelectDistrict)}
                  options={(districts || []).map((i) => ({
                    value: i.id,
                    label: i.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.post_code")}
                name={["address", "post_code"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField type="number" />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.street")}
                required
                rules={[{ required: true, message: t("salon.info.validation.street") }]}
                name={["address", "street"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.long")}
                name={["address", "lat"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.lat")}
                name={["address", "lon"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col span={24}>
              <GoogleMap
                setLat={(lat) => infoForm.setFieldValue("lat", lat)}
                setLong={(lon) => infoForm.setFieldValue("lon", lon)}
                lat={latLongPosition.lat}
                long={latLongPosition.lng}
              />
            </Col>
          </Row>
        </Box>

        {/* SOCIAL MEDIA */}
        <Box component={Card} mt={3} className="p-4">
          <Box fontWeight={"bold"} fontSize="20px" mb={2}>
            {t("salon.info.sections.social")}
          </Box>
          <Row gutter={[12, 12]}>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.website")}
                name={["social", "website"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: false, message: t("salon.info.validation.url_required") },
                  {
                    type: "url",
                    message: t("salon.info.validation.url_invalid"),
                  },
                ]}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.facebook")}
                name={["social", "facebook_url"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: false, message: t("salon.info.validation.url_required") },
                  {
                    type: "url",
                    message: t("salon.info.validation.url_invalid"),
                  },
                ]}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.instagram")}
                name={["social", "instagram_url"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: false, message: t("salon.info.validation.url_required") },
                  {
                    type: "url",
                    message: t("salon.info.validation.url_invalid"),
                  },
                ]}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.twitter")}
                name={["social", "twitter_url"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: false, message: t("salon.info.validation.url_required") },
                  {
                    type: "url",
                    message: t("salon.info.validation.url_invalid"),
                  },
                ]}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.zalo")}
                name={["social", "zalo_url"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: false, message: t("salon.info.validation.url_required") },
                  {
                    type: "url",
                    message: t("salon.info.validation.url_invalid"),
                  },
                ]}
              >
                <InputField />
              </Form.Item>
            </Col>
          </Row>
        </Box>

        {/* BANK INFO */}
        <Box component={Card} mt={3} className="p-4">
          <Box fontWeight={"bold"} fontSize="20px" mb={2}>
            {t("salon.info.sections.bank")}
          </Box>
          <Row gutter={[12, 12]}>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.routing_number")}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                name={["bank", "routing_number"]}
              >
                <InputField type="number" />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.account_number")}
                labelCol={{ span: 24 }}
                name={["bank", "account_number"]}
                wrapperCol={{ span: 24 }}
              >
                <InputField type="number" />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.account_holder")}
                name={["bank", "account_holder"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.bank_name")}
                name={["bank", "bank_name"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.bank_code")}
                name={["bank", "bank_code"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField type="number" />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.country")}
                name={["bank", "bank_country"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <SelectField
                  onChange={onSelectId(handleSelectCountryBankInfo)}
                  loading={loadingBankCountry}
                  options={(countryOfBank || []).map((i) => ({
                    value: i.id,
                    label: getCountryLabel(i),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.city_province")}
                labelCol={{ span: 24 }}
                name={["bank", "opening_branch"]}
                wrapperCol={{ span: 24 }}
              >
                <SelectField
                  loading={loadingCitiesBankInfo}
                  options={(cityOfBank || []).map((i) => ({
                    value: i.name,
                    label: i.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Box>

        {/* PERSON INCHARGE */}
        <Box component={Card} mt={3} className="p-4">
          <Box fontWeight={"bold"} fontSize="20px" mb={2}>
            {t("salon.info.sections.person")}
          </Box>
          <Row gutter={[12, 12]}>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.first_name")}
                required
                rules={[{ required: true, message: t("salon.info.validation.first_name") }]}
                name={["person_in_charge", "first_name"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.last_name")}
                required
                rules={[{ required: true, message: t("salon.info.validation.last_name") }]}
                labelCol={{ span: 24 }}
                name={["person_in_charge", "last_name"]}
                wrapperCol={{ span: 24 }}
              >
                <InputField />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.email")}
                required
                rules={[{ required: true, message: t("salon.info.validation.email"), type: 'email' }]}
                labelCol={{ span: 24 }}
                name={["person_in_charge", "email"]}
                wrapperCol={{ span: 24 }}
              >
                <InputField type="email" />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.phone_number")}
                required
                rules={[
                  { required: true, message: t("salon.info.validation.phone") },
                  {
                    validator: (_, value) => {
                      if (!value || value >= 0) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("salon.info.validation.phone_negative")));
                    }
                  }
                ]}
                name={["person_in_charge", "phone_number"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField type="number" inputProps={{ min: 0 }} />
              </Form.Item>
            </Col>
            <Col md={6} span={24} lg={6} xs={24}>
              <Form.Item
                label={t("salon.info.fields.language")}
                name={["person_in_charge", "language"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <SelectField
                  loading={languageLoading}
                  options={(language || []).map((i) => ({
                    value: i.id,
                    label: i.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}>
          {!id ? (
            <ActionButton onClick={handleContinue} disabled={isFormValid}>
              {t("salon.info.continue")}
            </ActionButton>
          ) : (
            <>
              <CancelButton>{t("btn.cancel")}</CancelButton>
              <ActionButton loading={loading} onClick={handleSubmit}>
                {t("btn.save")}
              </ActionButton>
            </>
          )}
        </Box>
      </Form>
      
    )}
    </>
  );
};

export default InfoTab;
